/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  FloatingPortal,
  useDismiss,
  useInteractions,
} from '@floating-ui/react'
import classNames from 'classnames'
import { User as UserIcon, WarningHexagon as WarningHexagonIcon } from 'iconoir-react'
import { isEmpty } from 'lodash'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PersonName } from '/app/components/activities/PersonName'
import { PersonOutcomes } from '/app/components/activities/PersonOutcomes'
import Spinner from '/app/components/Spinner'
import Tag from '/app/components/tags/Tag'
import type { ActivityJson } from 'app/types/entities'
import type { CallType, PersonSummary } from 'app/types/entities'
import type { CallAi } from 'app/generated/graphql'
import { gqlFetchOne } from 'app/lib/graphqlClient'
import { assertGood } from 'lib/assertBoom'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { run } from 'lib/run'
import { sortTags } from 'lib/sortTags'
import { callOutcomes } from 'types/CallDataExtraction'

type Props = { activity: ActivityJson; children: React.ReactElement; loadCallTooltip?: boolean }

type CallTooltipData = { ai: CallType[`ai`]; people: CallAi[`people`] }

let fetching: Record<string, Promise<CallTooltipData>> = {}
let cache: Record<string, CallTooltipData> = {}

export async function getCall(callId: string) {
  if (cache[callId] != null) return cache[callId]
  if (fetching[callId] != null) return fetching[callId]
  else if (callId == null || callId === `undefined`) return
  return (fetching[callId] = run(async () => {
    const data = await gqlFetchOne<{ ai: CallType[`ai`] }>(
      'call',
      callId,
      'ai { summary people { name phone patientType isCaller outcomes birthDate } }',
    )
    return (cache[callId] = { ai: data?.ai, people: data?.ai?.people })
  }))
}

/**
 * Renders which activity tags belong to which person identified in the call.
 *
 * @param activity Activity of Call type.
 * @param children React element that the tooltip will be applied to.
 * @returns Component with a tooltip for a tag that provides more information about the tag.
 */
export function CallTagTooltip({ activity, children, loadCallTooltip }: Props): ReactElement {
  const tags = activity.tags ?? []
  const [isHovered, setIsHovered] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [people, setPeople] = useState<PersonSummary[]>()
  const [ai, setAi] = useState<boolean>(false)

  const { floatingStyles, refs, context } = useFloating({
    placement: `right-start`,
    open: isHovered,
    onOpenChange: setIsHovered,
    middleware: [offset(8), flip({ fallbackAxisSideDirection: `start`, padding: 5 }), shift({ padding: 5 })],
    whileElementsMounted: autoUpdate,
  })

  const dismiss = useDismiss(context, { ancestorScroll: true })
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  assertGood(activity.type === `Call`, `CallTagTooltip should only be used with Call activities`)
  const tagsType = useMemo(() => {
    let response: { fromCall: string[]; fromUsers: string[]; fromActivity: string[] } = {
      fromCall: [],
      fromUsers: [],
      fromActivity: [],
    }
    if (people != null) {
      tags.forEach((tag) => {
        if (
          people?.find(
            (person) =>
              (person.outcomes?.includes(tag as any) && tag !== `New Patient`) ||
              [`Non-Patient`, `Current Patient`, `No Patient Found`].includes(tag),
          )
        ) {
          response = { ...response, fromCall: [...response.fromCall, tag] }
        } else if ([`Missed Call`, `AI`, `New Patient`, `Office Transfer`].includes(tag)) {
          response = { ...response, fromActivity: [...response.fromActivity, tag] }
        } else {
          response = { ...response, fromUsers: [...response.fromUsers, tag] }
        }
      })
    }
    debug([`calls`, `tags`, `outcomes`], `tags by call, users and activity`, response)
    // New Patient is next to the name in the UI, so we don't need to show in a sub-section
    response.fromActivity = response.fromActivity.filter((tag) => tag !== `New Patient`)
    return response
  }, [people, ai])

  useEffect(() => {
    if (loadCallTooltip && people == null) {
      bg(async () => {
        if (activity.typeId == null || people != null) return
        setIsLoading(true)
        await getCall(activity.typeId).then((data) => {
          setIsLoading(false)
          setPeople(data?.people ?? undefined)
          setAi(!isEmpty(data?.ai))
        })
      })
    }
  }, [loadCallTooltip])

  if (tags.length === 0) return <>{children}</>

  const isAbandonedOrInProgress = tags.length === 1 && (tags.includes(`Abandoned Call`) || tags.includes(`In Progress`))

  return (
    <div
      className="relative flex h-full items-center align-middle"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...getReferenceProps()}
    >
      <div
        ref={refs.setReference}
        className="flex flex-nowrap items-center space-x-1 overflow-hidden overflow-ellipsis whitespace-nowrap"
      >
        {children}
      </div>
      {isHovered && (
        <FloatingPortal>
          <div ref={refs.setFloating} style={floatingStyles} className="z-50" {...getFloatingProps()}>
            {isAbandonedOrInProgress ? (
              <div className="min-w-[300px] rounded-lg border bg-surface-raised p-2 shadow-elevation-5">
                <div className="flex flex-col text-strong">
                  <div className="inline-flex items-center gap-x-1">
                    <UserIcon height={16} width={16} className="text-default" />
                    <span className="text-ui-sm text-strong">Tags by People</span>
                  </div>
                  <div
                    className={classNames(
                      `mt-2 flex grow-0 flex-col gap-y-2 rounded-md border border-subtle py-2 pl-3 pr-4`,
                      {
                        'bg-rose-500/8': tags[0] === `Abandoned Call`,
                        'bg-amber-500/8': tags[0] === `In Progress`,
                      },
                    )}
                  >
                    <div className="flex items-center gap-x-1">
                      <WarningHexagonIcon
                        height={11}
                        width={12}
                        className={classNames({
                          'text-orange-800': tags[0] === `Call Abandonded` || tags[0] === `In Progress`,
                        })}
                      />
                      <span className="text-xs font-bold leading-[15px]">{tags[0]}</span>
                    </div>
                    <span className="grow-0 text-left text-body-xs text-default">
                      {tags[0] === `Abandoned Call` &&
                        `This call has been abandoned and therefore did not associate with a contact.`}
                      {tags[0] === `In Progress` &&
                        `This call recording is being transcribed and analyzed by AI to extract key insights.`}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border bg-surface-raised shadow-elevation-5">
                <div className="shrink-0">
                  {isLoading ? (
                    <div className="flex aspect-square h-10 w-full items-center justify-center align-middle">
                      <Spinner size={4} />
                    </div>
                  ) : ai && people ? (
                    <div className="min-w-72 flex flex-col p-2 text-strong">
                      {(tagsType.fromCall.length > 0 || tagsType.fromActivity.length > 0) && (
                        <>
                          <span className="text-left text-ui-sm text-strong">Tags from Call</span>
                          {people.map((person, i) =>
                            person.outcomes?.length === 1 && person.outcomes?.includes(`New Patient` as any) ? (
                              <React.Fragment key={`${person.name}-${i}`} />
                            ) : (
                              <div
                                key={`${person.name}-${i}`}
                                className="mt-2 flex grow-0 flex-col gap-y-2 rounded-md border bg-surface-subtle py-2 pl-3 pr-4"
                              >
                                <div className="flex w-full items-center gap-x-1">
                                  <PersonName person={person} isCaller={!!(person.isCaller && people.length > 1)} />
                                </div>
                                <PersonOutcomes
                                  activity={activity}
                                  person={{
                                    ...person,
                                    outcomes: sortTags(person.outcomes) as (keyof typeof callOutcomes)[],
                                  }}
                                  personIndex={i}
                                />
                              </div>
                            ),
                          )}
                          {tagsType.fromActivity.length > 0 && (
                            <div
                              key="other-tags"
                              className={classNames(
                                `flex grow-0 flex-col gap-y-2 rounded-md border bg-surface-subtle py-2 pl-3 pr-4`,
                                tagsType.fromCall.length > 0 || people?.length >= 1 ? `mt-2` : ``,
                              )}
                            >
                              <span className="text-trn-black text-left text-ui-xs">Other tags</span>
                              <div className="flex flex-wrap gap-2">
                                {tagsType.fromActivity?.map((tag, i) => (
                                  <Tag
                                    key={`${tag}-${i}`}
                                    workspaceId={activity.workspaceId}
                                    model="activities"
                                    tag={tag as string}
                                    removeTag={() => {}}
                                    tagClassName="text-body-xs leading-4"
                                    readOnly
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      {tagsType.fromUsers.length > 0 && (
                        <div
                          className={classNames(
                            `text-left`,
                            tagsType.fromCall.length > 0 || tagsType.fromActivity.length > 0 ? `mt-2` : ``,
                          )}
                        >
                          <span className="w-full text-left text-ui-sm text-strong">Tags from Users</span>
                          <div className="mt-2 flex grow-0 flex-col gap-y-2 rounded-md border bg-surface-subtle py-2 pl-3 pr-4">
                            <div className="flex flex-wrap gap-2">
                              {tagsType.fromUsers?.map((tag, i) => (
                                <Tag
                                  key={`${tag}-${i}`}
                                  workspaceId={activity.workspaceId}
                                  model="activities"
                                  tag={tag as string}
                                  removeTag={() => {}}
                                  tagClassName="text-body-xs leading-4"
                                  readOnly
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="min-w-72 flex flex-col p-2 text-strong">
                      <span className="text-left text-ui-sm text-strong">Tags from Call</span>
                      <div className="mt-2 flex grow-0 flex-col gap-y-2 rounded-md border bg-surface-subtle py-2 pl-3 pr-4">
                        <span className="text-trn-black text-left text-ui-xs">Other tags</span>
                        <div className="flex flex-wrap gap-2">
                          {sortTags(activity.tags)?.map((tag, i) => (
                            <Tag
                              key={`${tag}-${i}`}
                              workspaceId={activity.workspaceId}
                              model="activities"
                              tag={tag as string}
                              removeTag={() => {}}
                              tagClassName="text-body-xs leading-4"
                              readOnly
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </FloatingPortal>
      )}
    </div>
  )
}
